import React from "react";
import * as Sentry from "@sentry/react";
import { Box, Typography, Button, Card, Alert } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { useTheme } from "@mui/material/styles";

interface ErrorFallbackProps {
  error: Error;
  resetError?: () => void;
  componentStack?: string;
}

/**
 * Material-UI styled error fallback component for Sentry ErrorBoundary
 */
export const SentryErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  componentStack,
}) => {
  const theme = useTheme();
  const isDev = import.meta.env.DEV;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          p: 3,
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
        }}
      >
        {/* Error Icon */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <ErrorIcon
            sx={{
              fontSize: 60,
              color: theme.palette.error.main,
            }}
          />
        </Box>

        {/* Error Title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            textAlign: "center",
            mb: 1,
            fontWeight: "bold",
            color: theme.palette.text.primary,
          }}
        >
          Oops! Something went wrong
        </Typography>

        {/* Error Message */}
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mb: 2,
            color: theme.palette.text.secondary,
          }}
        >
          We've been notified about this issue and will look into it soon.
        </Typography>

        {/* Error Alert (Dev Only) */}
        {isDev && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              <strong>Error Details:</strong>
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                wordBreak: "break-all",
                whiteSpace: "pre-wrap",
                maxHeight: 150,
                overflow: "auto",
              }}
            >
              {error?.message}
              {componentStack && (
                <>
                  {"\n\nComponent Stack:\n"}
                  {componentStack}
                </>
              )}
            </Typography>
          </Alert>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {resetError && (
            <Button
              variant="contained"
              color="primary"
              onClick={resetError}
              sx={{ minWidth: 120 }}
            >
              Try Again
            </Button>
          )}

          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              Sentry.showReportDialog();
            }}
            sx={{ minWidth: 120 }}
          >
            Report Issue
          </Button>

          <Button
            variant="text"
            color="primary"
            onClick={() => {
              window.location.href = "/";
            }}
            sx={{ minWidth: 120 }}
          >
            Go Home
          </Button>
        </Box>

        {/* Support Message */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 2,
            color: theme.palette.text.disabled,
          }}
        >
          Error ID: {Sentry.lastEventId()}
        </Typography>
      </Card>
    </Box>
  );
};
